import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PublicAdoptionCard from '@/components/Adoption/PublicAdoptionCard';
import AdoptionRequestModal from '@/components/Adoption/AdoptionRequestModal';
import AdoptionMapView from '@/components/Adoption/AdoptionMapView';
import { Search, Filter, Heart, MapPin, List, Map } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import adoptionHeroBg from '@/assets/adoption-hero-bg.jpg';

interface AdoptionPost {
  id: string;
  title: string;
  pet_name: string;
  species: string;
  breed?: string;
  age?: number;
  size?: string;
  gender?: string;
  character_traits?: string;
  health_info?: string;
  description?: string;
  location: string;
  adoption_conditions?: string;
  image_url?: string;
  status: string;
  requests_count: number;
  created_at: string;
  user_id: string;
  latitude?: number;
  longitude?: number;
}

export default function Adoption() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Set document title and meta description for SEO
  useEffect(() => {
    document.title = 'Adozione Pet - Trova il Tuo Nuovo Amico | AstroPet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Trova cani, gatti e altri animali in adozione su AstroPet. Connettiti con proprietari responsabili, visualizza annunci dettagliati e dai una seconda possibilità a un pet bisognoso.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Trova cani, gatti e altri animali in adozione su AstroPet. Connettiti con proprietari responsabili, visualizza annunci dettagliati e dai una seconda possibilità a un pet bisognoso.';
      document.head.appendChild(meta);
    }
    
    // Set canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://astropet.it/adoption');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://astropet.it/adoption';
      document.head.appendChild(link);
    }
  }, []);
  const [posts, setPosts] = useState<AdoptionPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedPost, setSelectedPost] = useState<AdoptionPost | null>(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  // Get user location for nearby search
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);

  useEffect(() => {
    fetchAdoptionPosts();
    
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => console.error('Error getting location:', error)
      );
    }
  }, []);

  const fetchAdoptionPosts = async () => {
    try {
      // First get adoption posts
      const { data: adoptionData, error: adoptionError } = await supabase
        .from('adoption_posts')
        .select('*')
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (adoptionError) throw adoptionError;

      // Then get profiles for each user (guard against empty array)
      const rawUserIds = adoptionData?.map(post => post.user_id) || [];
      const userIds = Array.from(new Set(rawUserIds)).filter(Boolean) as string[];

      let profilesData: { user_id: string; username: string | null; display_name: string | null; avatar_url: string | null }[] = [];
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, username, display_name, avatar_url')
          .in('user_id', userIds);
        if (profilesError) throw profilesError;
        profilesData = profiles || [];
      }

      // Combine the data
      const data = (adoptionData || []).map(post => ({
        ...post,
        profiles: profilesData.find(profile => profile.user_id === post.user_id) || null,
      }));

      setPosts(data);
    } catch (error) {
      console.error('Error fetching adoption posts:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare gli annunci di adozione",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAdoption = (post: AdoptionPost) => {
    if (!user) {
      toast({
        title: "Accesso richiesto",
        description: "Devi effettuare l'accesso per fare una richiesta di adozione",
        variant: "destructive",
      });
      return;
    }

    setSelectedPost(post);
    setRequestModalOpen(true);
  };

  const handleContactOwner = async (post: AdoptionPost) => {
    if (!user) {
      toast({
        title: "Accesso richiesto",
        description: "Devi effettuare l'accesso per contattare il proprietario",
        variant: "destructive",
      });
      return;
    }

    if (!post.user_id) {
      toast({
        title: "Autore non disponibile",
        description: "Impossibile contattare l'autore di questo annuncio",
        variant: "destructive",
      });
      return;
    }

    try {
      // Cerca una conversazione diretta esistente tra i due utenti
      const { data: existingConversations, error: fetchError } = await supabase
        .from('conversation_participants')
        .select(`
          conversation_id,
          conversations (
            id,
            type,
            conversation_participants!inner (
              user_id
            )
          )
        `)
        .eq('user_id', user.id)
        .is('left_at', null);

      if (fetchError) {
        console.warn('Errore nel controllo conversazioni esistenti, procedo a crearne una nuova:', fetchError);
      }

      let conversationId: string | null = null;

      for (const participation of existingConversations || []) {
        const conversation = (participation as any).conversations as any;
        if (conversation?.type === 'direct') {
          const participants = conversation.conversation_participants as Array<{ user_id: string }>;
          if (participants?.length === 2) {
            const otherParticipant = participants.find(p => p.user_id !== user.id);
            if (otherParticipant?.user_id === post.user_id) {
              conversationId = conversation.id;
              break;
            }
          }
        }
      }

      // Se non esiste, crea una nuova conversazione e aggiungi i partecipanti
      if (!conversationId) {
        const { data: newConversation, error: createError } = await supabase
          .from('conversations')
          .insert({ type: 'direct', name: null, created_by: user.id })
          .select()
          .maybeSingle();

        if (createError || !newConversation) throw createError || new Error('Creazione conversazione fallita');
        conversationId = newConversation.id as string;

        const { error: participantsError } = await supabase
          .from('conversation_participants')
          .insert([
            { conversation_id: conversationId, user_id: user.id },
            { conversation_id: conversationId, user_id: post.user_id }
          ]);

        if (participantsError) throw participantsError;
      }

      // Vai alla chat
      navigate(`/app/chat/${conversationId}`);
    } catch (error) {
      console.error('Error creating/finding conversation:', error);
      toast({
        title: "Errore",
        description: "Errore nell'aprire la chat",
        variant: "destructive",
      });
    }
  };

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const filteredPosts = posts.filter((post) => {
    const matchesSearch = 
      post.pet_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.breed?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.location.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSpecies = speciesFilter === 'all' || post.species === speciesFilter;

    const matchesLocation = !locationFilter || 
      post.location.toLowerCase().includes(locationFilter.toLowerCase());

    return matchesSearch && matchesSpecies && matchesLocation;
  }).sort((a, b) => {
    // Sort by distance if user location is available and posts have coordinates
    if (userLocation && a.latitude && a.longitude && b.latitude && b.longitude) {
      const distanceA = calculateDistance(userLocation.latitude, userLocation.longitude, a.latitude, a.longitude);
      const distanceB = calculateDistance(userLocation.latitude, userLocation.longitude, b.latitude, b.longitude);
      return distanceA - distanceB;
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="glass border-white/20">
              <div className="aspect-video bg-muted animate-pulse" />
              <CardContent className="p-4 space-y-3">
                <div className="h-4 bg-muted rounded animate-pulse" />
                <div className="h-3 bg-muted rounded w-2/3 animate-pulse" />
                <div className="h-3 bg-muted rounded w-1/2 animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="cosmic-background min-h-screen w-full overflow-x-hidden"
      style={{ backgroundImage: `url(${adoptionHeroBg})` }}
    >
      <div className="max-w-6xl mx-auto p-2 sm:p-4 pb-20 lg:pb-4 w-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <Heart className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-white">Pet in Adozione</h1>
            <p className="text-muted-foreground">
              Trova il tuo nuovo migliore amico e dai una seconda possibilità
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="glass border-white/20">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per nome, razza o città..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Tutte le specie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le specie</SelectItem>
                  <SelectItem value="Cane">Cani</SelectItem>
                  <SelectItem value="Gatto">Gatti</SelectItem>
                  <SelectItem value="Coniglio">Conigli</SelectItem>
                  <SelectItem value="Altro">Altri</SelectItem>
                </SelectContent>
              </Select>

              <div className="relative w-full md:w-48">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Zona..."
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

        {/* View Mode Toggle */}
        <div className="flex items-center justify-between mb-6">
          <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'list' | 'map')} className="w-auto">
            <TabsList className="glass border-white/20">
              <TabsTrigger value="list" className="data-[state=active]:bg-primary">
                <List className="w-4 h-4 mr-2" />
                Lista
              </TabsTrigger>
              <TabsTrigger value="map" className="data-[state=active]:bg-primary">
                <Map className="w-4 h-4 mr-2" />
                Mappa
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {userLocation && (
            <p className="text-sm text-muted-foreground">
              📍 Ordinati per distanza dalla tua posizione
            </p>
          )}
        </div>

        {/* Content based on view mode */}
        {viewMode === 'map' ? (
          <AdoptionMapView onRequestAdoption={handleRequestAdoption} />
        ) : (
          <>
            {/* Results */}
            {filteredPosts.length === 0 ? (
              <Card className="glass border-white/20">
                <CardContent className="p-12 text-center">
                  <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {posts.length === 0 ? 'Nessun pet in adozione' : 'Nessun risultato'}
                  </h3>
                  <p className="text-muted-foreground">
                    {posts.length === 0 
                      ? 'Al momento non ci sono pet in cerca di una nuova casa'
                      : 'Prova a modificare i filtri di ricerca'
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-muted-foreground">
                    {filteredPosts.length} {filteredPosts.length === 1 ? 'pet trovato' : 'pet trovati'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPosts.map((post, index) => (
                    <div key={post.id} className="relative">
                      <PublicAdoptionCard 
                        post={post}
                        onRequestAdoption={handleRequestAdoption}
                        onContactOwner={handleContactOwner}
                        currentUserId={user?.id}
                      />
                      {/* Distance badge if available */}
                      {userLocation && post.latitude && post.longitude && (
                        <div className="absolute top-2 right-2 z-10">
                          <div className="bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                            ~{Math.round(calculateDistance(
                              userLocation.latitude, 
                              userLocation.longitude, 
                              post.latitude, 
                              post.longitude
                            ))} km
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

      {/* Request Modal */}
      <AdoptionRequestModal
        open={requestModalOpen}
        onOpenChange={setRequestModalOpen}
        post={selectedPost}
        onSuccess={() => {
          fetchAdoptionPosts();
        }}
      />
      </div>
    </div>
  );
}